import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';
import { clsx } from 'clsx';

import { chatApi } from '@/services/chat-api';
import type { NudgeCard } from '@/types/chat';

/**
 * PendingThreadCards
 *
 * Surfaces AI-initiated chat threads that are still 'pending' (the user
 * has not yet replied) as compact "nudge cards" on the chat home.
 * Each card is rendered from a backend trigger's `projectCard` output;
 * clicking navigates to the thread so the user can engage.
 *
 * Visual reference:
 *   bizguard-backend/docs/mockups/Flair B_ Insight Nudge Cards.html
 */

// Categorical nudge accents — map the backend's enum values onto our
// semantic status tokens. See DESIGN_SYSTEM_COLOR.md §3 / §4 (info/warn/
// success/alert + brand cyan for violet).
const ACCENT_STYLES: Record<NonNullable<NudgeCard['accent']>, string> = {
  blue:    'bg-info/15 text-info',
  amber:   'bg-warn/15 text-warn',
  emerald: 'bg-success/15 text-success',
  violet:  'bg-primary/15 text-primary',
  rose:    'bg-alert/15 text-alert',
};

function resolveIcon(name: string): React.ComponentType<{ className?: string }> {
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name];
  return Icon ?? LucideIcons.Sparkles;
}

const PendingThreadCards: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['chat-threads', 'pending'],
    queryFn: () => chatApi.listPendingThreads(10),
    staleTime: 30_000,
  });

  const cards: NudgeCard[] = data?.data ?? [];

  if (isLoading || cards.length === 0) return null;

  return (
    <div className="w-full max-w-[680px] mb-4 flex flex-col gap-2.5">
      {cards.map((card) => {
        const Icon = resolveIcon(card.icon);
        const accent = ACCENT_STYLES[card.accent ?? 'violet'];
        return (
          <button
            key={card.thread_id}
            type="button"
            onClick={() => navigate(`/chat/${card.thread_id}`)}
            className={clsx(
              'group flex items-center gap-3 px-4 py-3 rounded-xl text-left',
              'bg-background/60 border border-border',
              'hover:bg-accent dark:hover:bg-accent/30 hover:border-primary/40 dark:hover:border-primary/30',
              'transition-colors duration-150 shadow-sm',
            )}
            aria-label={`${card.title} — ${card.cta_label}`}
          >
            <div
              className={clsx(
                'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                accent,
              )}
            >
              <Icon className="w-4.5 h-4.5" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {card.title}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {card.subtitle ?? card.body}
              </div>
            </div>

            <span
              className={clsx(
                'text-xs font-medium px-2.5 py-1 rounded-md whitespace-nowrap',
                'text-primary',
                'group-hover:bg-accent',
              )}
            >
              {card.cta_label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PendingThreadCards;
